import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/shared/presentation/components/ui/Button';
import { DataTable } from '@/shared/presentation/components/ui/DataTable';
import { FormModal } from '@/shared/presentation/components/ui/FormModal';
import { Input } from '@/shared/presentation/components/ui/Input';
import { LoadingModal } from '@/shared/presentation/components/ui/LoadingDialog';
import { Page } from '@/shared/presentation/components/ui/Page';
import { TagInput } from '@/shared/presentation/components/ui/TagInput';

import { UserViewModel } from './User.vm';

export const AddRoleModal = observer(() => {
  const vm = useViewModel(UserViewModel);

  return (
    <FormModal
      title="Adicionar papel a conta"
      isSubmitting={vm.isAddingRole}
      isValid={vm.accessListForm.isValid}
      onClose={() => vm.closeAddRoleModal()}
      onSubmit={() => vm.addRoleToUser()}
    >
      <TagInput
        label="Papel"
        name="role"
        onChange={(values) => vm.accessListForm.setValueAndValidateValue('role', values[0])}
        options={vm.roles.map((role) => ({
          guid: role.guid,
          name: role.name,
        }))}
        defaultValue={vm.accessListForm.values.role ? [vm.accessListForm.values.role] : []}
        isMulti={false}
      />

      <TagInput
        label="Escopo"
        name="scopes"
        onChange={(value) => vm.accessListForm.setValueAndValidateValue('scopes', value)}
        options={vm.scopes.map((role) => ({
          guid: role.guid,
          name: role.name,
        }))}
        defaultValue={vm.accessListForm.values.scopes}
      />
    </FormModal>
  );
});

export const Roles = observer(() => {
  const vm = useViewModel(UserViewModel);

  const columns = useMemo(
    () => [
      {
        name: 'Nome',
        selector: (row) => row.role.name,
        sortable: true,
      },
      {
        name: 'Descrição',
        selector: (row) => row.description,
      },
      {
        name: 'Escopo',
        selector: (row) => row.scope.name,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        name: 'Remover',
        onClick: (row) => {
          const confirm = window.confirm('Tem certeza que deseja remover o papel?');
          if (confirm) {
            vm.removeRoleFromAccount(row);
          }
        },
      },
    ],
    [],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={vm.accessList.map((access) => ({
          ...access,
          id: `${access.role.guid}+${access.scope.guid}`,
        }))}
        actions={actions}
        headerActions={
          <Button
            icon="add"
            variant="secondary"
            size="small"
            onClick={() => {
              vm.openAddRoleModal();
            }}
          >
            Adicionar papel
          </Button>
        }
      />
      {vm.isRemovingAccessList && <LoadingModal text="Removendo papel..." />}
    </>
  );
});

export const AddTagModal = observer(() => {
  const vm = useViewModel(UserViewModel);

  return (
    <FormModal
      title="Adicionar tag"
      isSubmitting={vm.isAddingRole}
      isValid={vm.tagForm.isValid}
      onClose={() => vm.closeAddTagModal()}
      onSubmit={() => vm.addTagToAccount()}
    >
      <Input
        label="Nome"
        name="name"
        errorMessage={vm.tagForm.errors.name && vm.tagForm.errors.name[0]}
        onChange={(value) => vm.tagForm.setValue('name', value)}
        onBlur={() => vm.tagForm.validateField('name')}
        value={vm.tagForm.values.name}
      />
      <TagInput
        label="Escopo"
        name="scopes"
        isMulti={false}
        onChange={(value) => vm.tagForm.setValueAndValidateValue('scopes', value)}
        options={vm.scopes.map((role) => ({
          guid: role.guid,
          name: role.name,
        }))}
        defaultValue={vm.tagForm.values.scopes?.[0]}
      />
    </FormModal>
  );
});

export const Tags = observer(() => {
  const vm = useViewModel(UserViewModel);

  const columns = useMemo(
    () => [
      {
        name: 'Slug',
        selector: (row) => row.slug,
      },
      {
        name: 'Nome',
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: 'Escopo',
        selector: (row) => row.scope.name,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        name: 'Remover',
        onClick: (row) => {
          const confirm = window.confirm('Tem certeza que deseja remover a tag? ');
          if (confirm) {
            vm.removeTagFromAccount(row);
          }
        },
      },
    ],
    [],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={vm.tags.map((tag) => ({
          scope: tag.scope,
          name: tag.name,
          slug: tag.slug,
          id: tag.slug,
        }))}
        actions={actions}
        headerActions={
          <Button
            icon="add"
            variant="secondary"
            size="small"
            onClick={() => {
              vm.openAddTagModal();
            }}
          >
            Adicionar tag
          </Button>
        }
      />
      {vm.isRemovingTag && <LoadingModal text="Removendo tag..." />}
    </>
  );
});

const UserDetails = observer(() => {
  const vm = useViewModel(UserViewModel);

  if (!vm.user) {
    return null;
  }

  return (
    <div>
      <p>GUID: {vm.user?.registrationCode}</p>
      <p>Nome: {vm.user?.name}</p>
      <p>Email: {vm.user?.email}</p>
    </div>
  );
});

export const UserScreen = observer(() => {
  const params = useParams<{ registrationCode: string }>();
  const vm = useViewModel(UserViewModel, { registrationCode: params.registrationCode });

  return (
    <Page
      title={vm.user?.name}
      subtitle={vm.user?.email}
      isLoading={vm.isLoading}
      hasError={vm.hasError}
      errorMessage="Ocorreu um erro ao carregar a conta."
      tabs={[
        {
          label: 'Detalhes',
          content: <UserDetails />,
        },
        {
          label: 'Papéis',
          content: <Roles />,
        },
        {
          label: 'Tags',
          content: <Tags />,
        },
      ]}
      headerActions={() => (
        <>
          <Button
            loading={vm.isImpersonating}
            disabled={vm.isImpersonating}
            onClick={() => vm.impersonate()}
          >
            Personificar
          </Button>
        </>
      )}
    >
      {vm.isAddRoleModalOpen && <AddRoleModal />}
      {vm.isAddTagModalOpen && <AddTagModal />}
    </Page>
  );
});
